'use client';
import { ShortcutRegistrationsContext } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/internal/registryState.js';
import { resolveDisplayKeys } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/keybindingDisplay.js';
import { useShortcutRegistry } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/registry.js';
import { getShortcutGroupLabel, getShortcutGroupOrder, } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/shortcutGroups.js';
import { isShortcutVisibleOnCurrentDevice, SHORTCUT_MANIFEST, resolvePlatformKeybinding, } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/shortcutManifest.js';
import { useKeyboardHintStyle } from '#pplx/frontend/apps/ask/user-settings/hooks/useKeyboardHintStyle.js';
import { useContext, useMemo } from 'react';
/**
 * Produces a stable string key for deduplicating manifest entries that would
 * render as identical rows in the shortcuts modal.
 *
 * Multiple manifest entries can represent the same logical shortcut in
 * different contexts with the same keybinding. Without deduplication the modal
 * would display duplicate rows. This key combines the label ID, resolved display
 * keys, and mode qualifier so entries that look identical to the user collapse
 * into one.
 */
function getDedupeKey(def, displayKeys) {
    return `${String(def.label.id)}|${displayKeys.join(',')}|${def.mode ?? ''}`;
}
/**
 * Returns shortcut definitions from the manifest, organized into sections.
 *
 * Shortcuts without live registrations still render as inactive manifest
 * entries. Shortcuts with live registrations are hidden when every registration
 * for that ID is unavailable, so feature-gated shortcuts do not advertise
 * inactive bindings. Entries with identical label, displayKeys, and mode are
 * deduplicated via {@link getDedupeKey} (collapsed into a single row) with the
 * active state merged (active if *any* duplicate is active).
 *
 * This preserves the manifest fallback, unlike a runtime-only approach that
 * would only show shortcuts currently mounted via `useRegisterShortcut`.
 */
export function useAllShortcutDefinitions() {
    const { isMacOS, isMobileDevice } = useShortcutRegistry();
    const registrations = useContext(ShortcutRegistrationsContext);
    const hintStyle = useKeyboardHintStyle();
    return useMemo(() => {
        // Build sets of currently registered, available, and active shortcut IDs from the live registrations.
        const registeredIds = new Set(registrations.map((r) => r.id));
        const availableIds = new Set(registrations.filter((r) => r.isAvailable !== false).map((r) => r.id));
        const activeIds = new Set(registrations.filter((r) => r.isEnabled !== false).map((r) => r.id));
        // -- Pass 1: Deduplicate manifest entries that resolve to identical rows. --
        // Multiple manifest entries may share the same label + keybinding + mode.
        // We keep only the first occurrence and merge the active state so the row
        // shows as active if *any* underlying registration is mounted.
        const seen = new Map();
        for (const [id, entry] of Object.entries(SHORTCUT_MANIFEST)) {
            const def = { id, ...entry };
            if (!isShortcutVisibleOnCurrentDevice(def, isMobileDevice)) {
                continue;
            }
            if (registeredIds.has(def.id) && !availableIds.has(def.id)) {
                continue;
            }
            const keybinding = resolvePlatformKeybinding(def.defaultKeybinding, isMacOS);
            const displayKeys = resolveDisplayKeys(keybinding, hintStyle);
            const isActive = activeIds.has(def.id);
            const key = getDedupeKey(def, displayKeys);
            const existing = seen.get(key);
            if (existing) {
                if (isActive && !existing.entry.isActive) {
                    existing.entry.isActive = true;
                }
                continue;
            }
            seen.set(key, {
                entry: { label: def.label, displayKeys, isActive, mode: def.mode },
                group: def.group,
            });
        }
        // -- Pass 2: Bucket deduplicated entries into groups. --
        const buckets = new Map();
        for (const { entry, group } of seen.values()) {
            let bucket = buckets.get(group);
            if (!bucket) {
                bucket = [];
                buckets.set(group, bucket);
            }
            bucket.push(entry);
        }
        const sections = [...buckets.entries()]
            .sort(([a], [b]) => getShortcutGroupOrder(a) - getShortcutGroupOrder(b))
            .map(([group, shortcuts]) => ({
            group,
            heading: getShortcutGroupLabel(group),
            shortcuts,
        }));
        return sections;
    }, [isMacOS, isMobileDevice, registrations, hintStyle]);
}
//# sourceMappingURL=useAllShortcutDefinitions.js.map